import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import BulkUploadHeader from '../components/BulkUploadHeader/BulkUploadHeader';
import { DOWNLOAD_QUOTES_STEPS } from './constants';
import DownloadQuotesStep0 from './components/DownloadQuotesStep0/DownloadQuotesStep0';
import DownloadQuotesStep1 from './components/DownloadQuotesStep1/DownloadQuotesStep1';
import { IUploadResults, QUOTE_TYPE } from 'types/bulkUpload';
import { templateWithErrorsInfo } from './content/donwloadUploadInfo';
import { templateUploadedInfo } from './content/donwloadUploadInfo';
import { IUploadInfo } from '../UploadCatalog/content/uploadCatalogInfo';
import DownloadQuotesStep2 from './components/DownloadQuotesStep2/DownloadQuotesStep2';
import { uploadQuotes } from 'services/bulkUpload/uploadQuotes.service';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import './DonwloadUploadQuotes.scss';

const DonwloadUploadQuotesModule = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedQuoteType, setSelectedQuoteType] = useState<QUOTE_TYPE>();
  const [goBackText, setGoBackText] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [errorTemplateURL, setErrorTemplateURL] = useState<string | undefined>();
  const [uploadResults, setUploadResults] = useState<IUploadResults | undefined>();
  const { push } = useHistory();

  const steps = DOWNLOAD_QUOTES_STEPS;
  const nextStep = () => setCurrentStep(curr => curr + 1);
  const prevStep = () => setCurrentStep(curr => curr - 1);

  const handleSubmitUploadForm = async (file: File) => {
    try {
      if (!selectedQuoteType) throw new SyntaxError('No se ha seleccionado un tipo de cotización');
      const uploadQuotesResponse = await uploadQuotes(file, selectedQuoteType);
      setErrors(uploadQuotesResponse?.errors || []);
      setUploadResults(uploadQuotesResponse);
      setErrorTemplateURL(uploadQuotesResponse?.errorFileUrl);
      setCurrentStep(curr => curr + 1);
    } catch (error) {
      const errorMessage =
        error instanceof SyntaxError ? error.message : 'Error al subir las cotizaciones, por favor, inténtelo de nuevo';
      displayErrorNotification({
        customJSXMessage: <>{errorMessage}</>,
        duration: 5000
      });
    }
  };

  const handleBack = () => {
    if (currentStep === 0) {
      push('/bulk-upload/type-of-charge');
    } else {
      prevStep();
    }
  };

  const summaryUploadInfo = useMemo<IUploadInfo>(() => {
    const updatedRecords = uploadResults?.updatedRecords || 0;
    const totalRecords = uploadResults?.totalRecords || 0;
    if (errors && errors.length > 0) {
      return templateWithErrorsInfo(updatedRecords, totalRecords);
    }
    return templateUploadedInfo(updatedRecords, totalRecords);
  }, [errors, uploadResults]);

  const handleReturnToUpload = () => {
    prevStep();
  };

  const renderComponentInStep = (step: number) => {
    switch (step) {
      case 0:
        return (
          <DownloadQuotesStep0
            selectedQuoteType={selectedQuoteType}
            setSelectedQuoteType={setSelectedQuoteType}
            nextStep={nextStep}
          />
        );
      case 1:
        return (
          <DownloadQuotesStep1
            selectedQuoteType={selectedQuoteType}
            setSelectedQuoteType={setSelectedQuoteType}
            handleSubmitUploadForm={handleSubmitUploadForm}
            templateUrlWithErrors={errorTemplateURL}
          />
        );
      case 2:
        return (
          <DownloadQuotesStep2
            errors={errors}
            summaryUploadInfo={summaryUploadInfo}
            handleReturnToUpload={handleReturnToUpload}
            templateUrl={errorTemplateURL || ''}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    switch (currentStep) {
      case 0:
        setGoBackText('Volver a Cargue');
        break;
      case 1:
        setGoBackText(steps[0]);
        break;
      case 2:
        setGoBackText(steps[1]);
        break;
      default:
        setGoBackText('');
    }
  }, [currentStep, steps]);

  return (
    <main className="downloadUploadQuotesContainer">
      <BulkUploadHeader steps={steps} currentStep={currentStep} handleBack={handleBack} goBackText={goBackText} />
      {renderComponentInStep(currentStep)}
    </main>
  );
};

export default DonwloadUploadQuotesModule;
